import React, { useMemo } from "react";
import loadable from "@loadable/component";
import { Box, Column } from "@vp/swan";
import { ProductBuilderSelection } from "../../../../types/ProductBuilderTypes";
import { calculateColumnSpan, getTextBoxPadding } from "../../../../utils/RangeAttributes";
import { useExperimentsContext } from "../../../../contexts/ExperimentsProvider";
import DropdownSelector from "./DropdownSelector";
import TextBoxSelector from "./TextBoxSelector";
const TextButtonSelector = loadable(() => import("./TextButtonSelector"));
const ColorSwatchSelector = loadable(() => import("./ColorSwatchSelector"));
import "../../styles/Selector.scss";
import { Test2Selectors } from "./Test2Selectors";
import { isOptionVisible } from "../../../../utils/ProductBuilderHelper";

interface SelectorProps {
  builderSelection: ProductBuilderSelection;
  selectorIndex: number;
  disabled: boolean;
  removePadding: boolean;
  onChange: (value: string | null) => void;
}

const Selector = (props: SelectorProps) => {
  const { builderSelection: selection, selectorIndex, removePadding, disabled, onChange } = props;
  const selectionStyle = selection?.selectionStyle;

  const { useExperiment } = useExperimentsContext();

  if (!isOptionVisible(selection)) {
    return <></>;
  }

  const variation = useExperiment({
    experimentKey: "pdp_ui_test_2_-_vertical_selection_-_buttons",
    renderTestExperienceForVariations: ["control", "test"],
    fireImpressionForVariations: ["control", "test"],
    supportedMpvIds: ["flyers"],
    supportedLocales: ["en-US", "es-US"],
  });

  const columnSpan = useMemo(() => calculateColumnSpan(selectionStyle, selection), [
    selectionStyle,
    selection,
  ]);

  const textBoxPadding = useMemo(
    () => getTextBoxPadding(selectionStyle, removePadding, selection),
    [selectionStyle, removePadding, selection]
  );

  if (variation === "test") {
    return <Test2Selectors {...props} />;
  }

  const showIncompatibleValue = selectorIndex === 0;

  return (
    <Column span={columnSpan} spanXs={columnSpan} className={`selector-column ${textBoxPadding}`}>
      <Box mb={selectionStyle === "Text buttons" ? 2 : selectionStyle === "Text box" ? 0 : 5}>
        {selectionStyle === "Dropdown" && (
          <DropdownSelector
            selectionData={selection}
            showIncompatibleValues={showIncompatibleValue}
            onChange={onChange}
            disabled={disabled}
            selectorIndex={selectorIndex}
          />
        )}
        {selectionStyle === "Text buttons" && (
          <TextButtonSelector
            selectionData={selection}
            showIncompatibleValues={showIncompatibleValue}
            onChange={onChange}
            disabled={disabled}
            selectorIndex={selectorIndex}
          />
        )}
        {selectionStyle === "Swatches" && (
          <ColorSwatchSelector
            selectionData={selection}
            showIncompatibleValues={showIncompatibleValue}
            onChange={onChange}
            disabled={disabled}
            selectorIndex={selectorIndex}
          />
        )}
        {selectionStyle === "Text box" && (
          <TextBoxSelector selectionData={selection} onChange={onChange} disabled={disabled} />
        )}
      </Box>
    </Column>
  );
};

export default Selector;
